/**
 * The candidate domain's client-side vocabulary, and the two flows the screen cannot
 * express as a plain `useApiQuery`/`useApiMutation`: **register-then-upload** a CV, and
 * the **access-reason-gated download** of one.
 *
 * ── Why the download and upload are functions here, not hooks in the screen ──
 *
 * Both are two-step and neither fits the one-request shape of `useApiMutation`. Upload
 * is `POST …/documents` (register, which returns a presigned PUT URL) *then* a `PUT`
 * of the bytes straight to object storage. Download is a `fetch` with `X-Access-Reason`
 * whose `302` the browser follows to a signed GET — a call `api/client.ts` makes with
 * its own `sensitiveDownload`, because the shared `request` refuses redirects. Keeping
 * the orchestration here, behind small pure-ish functions, is what makes both testable
 * without a DOM (the screen injects the one side effect each has).
 *
 * ── The reason is checked here before a request goes out ────────────────────
 *
 * `reason.ts`'s whole argument: a screen that fires a request it knows will be refused
 * teaches people that refusals are noise. So `downloadCandidateDocument` validates the
 * reason with `isUsableReason` and, when it is missing or too short, returns the
 * `WORKFORCE_ACCESS_REASON_REQUIRED` refusal **without a request** — which is the same
 * code the server would answer, and which `ApiErrorNote` renders as the permission-
 * denied state. Fail closed, and learn the rule from the field.
 */

import type { ApiClient } from '../api/client';
import { CLIENT_TRANSPORT_FAILED, WorkforceApiError } from '../api/errors';
import type { MessageKey } from '../i18n/messages';
import {
  recruitingCandidateDocumentDownloadPath,
  recruitingCandidateDocumentsPath,
  type CandidateDocumentType,
  type CandidateSource,
  type RecruitingCandidateDocument,
  type RecruitingCandidateDocumentRegisterRequest,
  type RecruitingCandidateDocumentUpload,
} from '../api/recruiting';
import { isUsableReason } from '../access/reason';

/** The name of a candidate's `source`, for the row and detail meta. */
export const SOURCE_LABEL: Readonly<Record<CandidateSource, MessageKey>> = {
  career_site: 'recruiting.candidates.source.career_site',
  referral: 'recruiting.candidates.source.referral',
  agency: 'recruiting.candidates.source.agency',
  manual: 'recruiting.candidates.source.manual',
};

/** The name of a document's `doc_type`, for the documents list and the register form. */
export const DOC_TYPE_LABEL: Readonly<Record<CandidateDocumentType, MessageKey>> = {
  cv: 'recruiting.candidates.docType.cv',
  cover_letter: 'recruiting.candidates.docType.cover_letter',
  portfolio: 'recruiting.candidates.docType.portfolio',
  other: 'recruiting.candidates.docType.other',
};

/** A candidate whose PII has been erased. `redacted_at` is the server's own stamp. */
export function isRedacted(candidate: { readonly redacted_at: string | null }): boolean {
  return candidate.redacted_at !== null;
}

/* ─────────────────────────────────────────────── register + upload a CV (WP-W2) ── */

/** What the screen may know about a register-and-upload that has run or is running. */
export type UploadState =
  | { readonly status: 'idle' }
  | { readonly status: 'working' }
  | { readonly status: 'done'; readonly document: RecruitingCandidateDocument }
  | { readonly status: 'failed'; readonly error: WorkforceApiError };

/**
 * Register a CV's metadata, then upload its bytes to the presigned PUT URL the register
 * answered with. Two awaits, one outcome: a failure in either step is the one `error`
 * the form shows, and the bytes never touch this app's server (`openapi.yaml`: "The
 * server never proxies the bytes").
 *
 * `content_type` and `file_size_bytes` are sent only when the browser knows them, per
 * the present-or-absent rule — an empty `File.type` is omitted, not sent as `''`.
 */
export async function registerAndUploadDocument(
  client: Pick<ApiClient, 'request' | 'putPresigned'>,
  candidateId: string,
  file: File,
  docType: CandidateDocumentType,
): Promise<UploadState> {
  try {
    const body: RecruitingCandidateDocumentRegisterRequest = {
      filename: file.name,
      doc_type: docType,
      ...(file.type === '' ? {} : { content_type: file.type }),
      ...(Number.isFinite(file.size) ? { file_size_bytes: file.size } : {}),
    };
    const upload = await client.request<RecruitingCandidateDocumentUpload>(
      recruitingCandidateDocumentsPath(candidateId),
      { method: 'POST', body },
    );
    // Spent immediately and never stored — a bearer credential with a short life.
    await client.putPresigned(upload.upload_url, file);
    return { status: 'done', document: upload.document };
  } catch (cause) {
    return { status: 'failed', error: asWorkforceError(cause, 'upload failed') };
  }
}

/* ──────────────────────────────────── the access-reason-gated CV download (WP-W2) ── */

/** How the page delivers the fetched bytes to the operator. Injected so a test drives it. */
export type DeliverBlob = (blob: Blob, filename: string) => void;

/** What a screen may remember about a download it asked for. Deliberately holds no URL. */
export type DownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly docId: string }
  | { readonly status: 'delivered'; readonly docId: string; readonly filename: string }
  | { readonly status: 'refused'; readonly docId: string; readonly error: WorkforceApiError };

/**
 * The `403 WORKFORCE_ACCESS_REASON_REQUIRED` this client raises **itself** when the
 * reason is missing or too short — same code the server answers, so `ApiErrorNote`
 * renders the same permission-denied state whichever raised it. The message is never
 * shown (`PermissionDenied` keys off the code), but it is a true sentence anyway.
 */
export function accessReasonRefusal(): WorkforceApiError {
  return new WorkforceApiError({
    code: 'WORKFORCE_ACCESS_REASON_REQUIRED',
    message: 'An access reason of at least ten characters is required to open a CV.',
    status: 403,
  });
}

/**
 * Open one candidate document, on the record.
 *
 * The reason is validated **before** a request goes out; a bad one is refused here with
 * no `wp_data_access_logs` row possible, because none is written for a request that was
 * never made. A good one is sent — trimmed, as the server trims before measuring — as
 * `X-Access-Reason`; the browser follows the `302` and the bytes are handed to
 * `deliver`. The signed URL never reaches this function.
 */
export async function downloadCandidateDocument(
  client: Pick<ApiClient, 'sensitiveDownload'>,
  candidateId: string,
  doc: RecruitingCandidateDocument,
  reason: string,
  deliver: DeliverBlob,
): Promise<DownloadState> {
  if (!isUsableReason(reason)) {
    return { status: 'refused', docId: doc.id, error: accessReasonRefusal() };
  }
  try {
    const blob = await client.sensitiveDownload(
      recruitingCandidateDocumentDownloadPath(candidateId, doc.id),
      reason.trim(),
    );
    deliver(blob, doc.filename);
    return { status: 'delivered', docId: doc.id, filename: doc.filename };
  } catch (cause) {
    return { status: 'refused', docId: doc.id, error: asWorkforceError(cause, 'download failed') };
  }
}

/**
 * The browser side effect `downloadCandidateDocument` is handed in production: turn the
 * blob into a same-origin object URL, click an anchor to save it under the document's
 * own filename, then revoke the URL. It is separated from the orchestration so the
 * orchestration is testable without `URL.createObjectURL`, which jsdom does not
 * implement — the screen passes this; a test passes a spy.
 */
export function deliverBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function asWorkforceError(cause: unknown, fallback: string): WorkforceApiError {
  return cause instanceof WorkforceApiError
    ? cause
    : new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : fallback,
        status: 0,
      });
}
